import type {ElementId, GData, getColorForValueOptions, Group} from './types';

type methodAutoGroupName = 'fromElementNames' | 'fromGivenName';

interface GroupData {
    data: GData;
    load(data: GData): this;
    addGroup(group: Group): number;
    getGroup(id: number): Group | null;
    getGroups(): Group[];
    updateGroup(group: Group): Group;
    removeGroup(id: number): void;
    getColorForValue(id: ElementId, options: getColorForValueOptions): string;
    countGroups(): number;
    getData(): GData;
    addElementToGroup(groupId: number, elementId: ElementId): void;
    removeElementFromGroup(groupId: number, elementId: ElementId): void;
    getSelectedElements(group_id: number): ElementId[];
    getGroupsNames(): string[];
}

class GroupData implements GroupData {

    data: GData = {
        groups: [],
        without_filter_ids: [],
        with_filter_ids: [],
        selected_option: null,
    };

    load(data: GData): this {
        this.data = JSON.parse(JSON.stringify(data));
        return this;
    }

    setTypeName(typeName: string): this {
        this.data.selected_option = typeName;
        return this;
    }

    getTypeName(): string {
        return this.data.selected_option;
    }

    getData(): GData {
        return JSON.parse(JSON.stringify(this.data));
    }

    getGroup(id: number): Group | null {
        if (typeof this.data.groups === 'undefined') {
            return null;
        } else {
            return this.data?.groups.find((group) => group.id === id) || null;
        }
    }

    getGroups(): Group[] {
        return this.data.groups || [];
    }

    addGroup(group: Group): number {
        let nextId = this.getBlockNextId();
        group.id = nextId;
        if (!this.data.groups) {
            this.data.groups = [];
        }
        this.data.groups.push(group);
        return group.id;
    }

    getBlockNextId() {
        if (!this.data.groups) {
            return 0;
        }
        let findMaxIdInBlocks = 0;
        this.data.groups.forEach(group => {
            if (group.id > findMaxIdInBlocks) {
                findMaxIdInBlocks = group.id;
            }
        });
        return findMaxIdInBlocks + 1;
    }

    getColorForValue(id: ElementId, options: getColorForValueOptions): string {
        let color = '';
        let group = this.data.groups.find((group) => group.element_ids.includes(id));
        if (group) {
            color = group.color;
        }
        if (options && options.withoutHash === true) {
            color = color.replace('#', '');
        }
        return color;
    }

    countGroups(): number {
        return this.data.groups.length;
    }

    updateGroup(group: Group): Group {
        let groupIndex = this.data.groups.findIndex((g) => g.id === group.id);
        this.data.groups[groupIndex] = group;
        return group;
    }

    removeGroup(id: number): void {
        let groupIndex = this.data.groups.findIndex((g) => g.id === id);
        this.data.groups.splice(groupIndex, 1);
    }

    addElementToGroup(groupId: number, elementId: ElementId): void {
        if (elementId !== null) {
            let group = this.getGroup(groupId);
            if (group) {
                group.element_ids.push(elementId);
            }
        } else {
            console.warn('ElementId is null', groupId, elementId);
        }
    }

    removeElementFromGroup(groupId: number, elementId: ElementId): void {
        let group = this.getGroup(groupId);
        if (group) {
            let elementIndex = group.element_ids.findIndex((id) => id === elementId);
            group.element_ids.splice(elementIndex, 1);
        }
    }
    
    isNameAutoCreated(groupId: number): boolean {
        let group = this.getGroup(groupId);
        if (group) {
            return group.is_name_auto_created;
        }
        return false;
    }

    getSelectedElements(groupId: number): ElementId[] {
        let group = this.getGroup(groupId);
        if (group) {
            return group.element_ids;
        }
        return [];
    }
    
    getSelectedElementsInAnotherGroups(groupId: number): ElementId[] {
        let elements: ElementId[] = [];
        this.data.groups.forEach((group) => {
            if (group.id !== groupId) {
                elements = elements.concat(group.element_ids);
            }
        });
        return elements;
    }
    
    // getAvailableElementsNotSelectedInOtherGroups(groupId: number): ElementId[] {
    //     let elements: ElementId[] = [];
    //     this.data.groups.forEach((group) => {
    //         if (group.id !== groupId) {
    //             elements = elements.concat(group.element_ids);
    //         }
    //     });
    //     return elements;
    // }

    setGroupName(groupId: number, name: string): void {
        let group = this.getGroup(groupId);
        if (group) {
            group.name = name;
            group.is_name_auto_created = false;
        }
    }
    
    makeAutoGroupNameFromElementNames(groupId: number, elementNames: string[]): void {
        let group = this.getGroup(groupId);
        if (group) {
            // make group name from first elements names
            let name = elementNames[0] || 'Nowa grupa';
            if (elementNames.length > 1) {
                // name += ' (+' + (elementNames.length - 1) + ')';
                switch (elementNames.length-1) {
                    case 1:
                        name += ' i ' + (elementNames[1]);
                        break;
                    case 2:
                    case 3:
                    case 4:
                        name += ' oraz ' + (elementNames.length-1) + ' inne';
                        break;
                    default:
                        name += ' oraz ' + (elementNames.length-1) + ' innych';
                }
            }
            group.name = name;
        }
    }
    
    checkIfGroupNameAlreadyExists(name: string): boolean {
        let group = this.data.groups.find((group) => group.name === name);
        return group !== undefined;
    }
    
    makeAutoGroupNameFromGivenName(groupId: number, defaultName: string): void {
        let group = this.getGroup(groupId);
        if (group) {
            // make group name from first elements names
            let name = defaultName;
            // If name exist add number
            let nameExist = this.checkIfGroupNameAlreadyExists(name);
            if (nameExist) {
                let i = 1;
                while (nameExist) {
                    name = defaultName + ' (' + i + ')';
                    nameExist = this.checkIfGroupNameAlreadyExists(name);
                    i++;
                }
            }
            group.name = name;
        }
    }
    
    makeAutoGroupName(nameType: methodAutoGroupName, groupId: number, elementNames: string[], defaultName: string): void {
        switch (nameType) {
            case 'fromElementNames':
                this.makeAutoGroupNameFromElementNames(groupId, elementNames);
                break;
            case 'fromGivenName':
                this.makeAutoGroupNameFromGivenName(groupId, defaultName);
                break;
        }
    }
    
    setGroupNameAutoCreated(groupId: number, isNameAutoCreated: boolean): void {
        let group = this.getGroup(groupId);
        if (group) {
            group.is_name_auto_created = isNameAutoCreated;
        }
    }

    setGroupColor(groupId: number, color: string): void {
        let group = this.getGroup(groupId);
        if (group) {
            group.color = color;
        }
    }
    
    getGroupsNames(): string[] {
        let names: string[] = [];
        this.data.groups.forEach((group) => {
            names.push(group.name);
        });
        return names;
    }
    
}

export default GroupData;